import APIConstant from 'constants/ApiConstant';
import notify from 'constants/notificationConstants';
import { makeAutoObservable, runInAction } from 'mobx';
import { getRequest, postRequest } from 'utils/apiUtils';
import { getValidDataFromResponse, isValidArray, sendNotification } from 'utils/utilities';

/**
 * The backend may hand a template back either flat (`{ ...header, questions: [] }`)
 * or split (`{ template: {...}, questions: [] }`). Normalize both into the flat
 * shape `DynamicSurveyForm` consumes.
 */
const normalizeTemplateResult = result => {
	if (!result) return null;

	const payload = Array.isArray(result) ? result[0] : result;

	if (!payload) return null;

	const header = payload.template && typeof payload.template === 'object' ? payload.template : payload;
	const questions = payload.questions || payload.template_questions || header.questions || [];

	return { ...header, questions: isValidArray(questions) ? questions : [] };
};

/**
 * Submission history can arrive either already nested
 * (`[{ id, answers: [...] }]`) or flat, one row per answer with a
 * `submission_id`. Collapse the flat shape into the nested one.
 */
const normalizeSubmissions = result => {
	if (!isValidArray(result)) return [];

	const hasNestedAnswers = result.some(row => Array.isArray(row.answers));

	if (hasNestedAnswers) {
		return result.map(row => ({ ...row, answers: Array.isArray(row.answers) ? row.answers : [] }));
	}

	const isFlatAnswerRow = result.some(row => row.submission_id !== undefined && (row.question_text !== undefined || row.question_id !== undefined));

	if (!isFlatAnswerRow) {
		return result.map(row => ({ ...row, answers: [] }));
	}

	const grouped = [];
	const indexBySubmissionId = {};

	result.forEach(row => {
		const key = `${row.submission_id}`;

		if (indexBySubmissionId[key] === undefined) {
			indexBySubmissionId[key] = grouped.length;
			grouped.push({
				id: row.submission_id,
				respondent_name: row.respondent_name,
				respondent_type: row.respondent_type,
				submission_month: row.submission_month,
				created_date: row.submission_created_date || row.created_date,
				answers: [],
			});
		}

		grouped[indexBySubmissionId[key]].answers.push({
			question_id: row.question_id,
			question_text: row.question_text,
			question_type: row.question_type,
			answer_text: row.answer_text,
			answer_json: row.answer_json,
		});
	});

	return grouped;
};

class SurveySubmissionStore {
	assignedTemplates = [];
	isLoadingAssignedTemplates = false;

	submissionTemplate = null;
	isLoadingSubmissionTemplate = false;

	currentMonthSummary = null;
	isLoadingCurrentMonthSummary = false;

	isSubmittingSurvey = false;

	monthlySummaries = [];
	isLoadingMonthlySummaries = false;

	monthSubmissions = [];
	isLoadingMonthSubmissions = false;

	constructor() {
		makeAutoObservable(this);
	}

	/** Templates a facility manager is allowed to submit for the selected facility. */
	getAssignedTemplatesByFacility = async (facilityId, params = {}) => {
		try {
			this.setIsLoadingAssignedTemplates(true);

			const { response } = await getRequest({ url: `${APIConstant.SURVEY_TEMPLATES_BY_FACILITY}/${facilityId}`, extraParameters: { params } });

			const { result, error } = getValidDataFromResponse({ response, isRequired: false });

			if (!error && isValidArray(result)) {
				this.setAssignedTemplates(result);
			} else {
				this.setAssignedTemplates();
			}

			this.setIsLoadingAssignedTemplates();
			return this.assignedTemplates;
		} catch (error) {
			console.log('error :>> ', error);
			this.setAssignedTemplates();
			this.setIsLoadingAssignedTemplates();
			return [];
		}
	};

	getTemplateForSubmission = async templateId => {
		try {
			this.setIsLoadingSubmissionTemplate(true);
			this.setSubmissionTemplate(null);

			const { response } = await getRequest({ url: `${APIConstant.SURVEY_TEMPLATE}/${templateId}` });

			const { result, error } = getValidDataFromResponse({ response, isRequired: false });

			const template = error ? null : normalizeTemplateResult(result);

			this.setSubmissionTemplate(template);
			this.setIsLoadingSubmissionTemplate();

			return template;
		} catch (error) {
			console.log('error :>> ', error);
			this.setSubmissionTemplate(null);
			this.setIsLoadingSubmissionTemplate();
			return null;
		}
	};

	/** Non-blocking "N submissions already recorded this month" hint. */
	getCurrentMonthSummary = async (params = {}) => {
		try {
			this.setIsLoadingCurrentMonthSummary(true);

			const { response } = await getRequest({ url: APIConstant.SURVEY_MONTHLY_SUMMARY, extraParameters: { params } });

			const { result, error } = getValidDataFromResponse({ response, isRequired: false });

			const summary = error ? null : Array.isArray(result) ? result[0] || null : result || null;

			this.setCurrentMonthSummary(summary);
			this.setIsLoadingCurrentMonthSummary();

			return summary;
		} catch (error) {
			console.log('error :>> ', error);
			this.setCurrentMonthSummary(null);
			this.setIsLoadingCurrentMonthSummary();
			return null;
		}
	};

	submitSurvey = async body => {
		try {
			this.setIsSubmittingSurvey(true);

			const { response } = await postRequest({ url: APIConstant.SURVEY_SUBMISSION, payload: body });

			const { result, error } = getValidDataFromResponse({ response });

			if (error) throw new Error(notify.something_went_wrong);

			this.setIsSubmittingSurvey();
			return result;
		} catch (error) {
			sendNotification({ type: notify.error, message: notify.something_went_wrong });
			this.setIsSubmittingSurvey();
			throw error;
		}
	};

	/** Monthly aggregate rows shown in the Survey History tab. */
	getMonthlySummaries = async (params = {}) => {
		try {
			this.setIsLoadingMonthlySummaries(true);

			const { response } = await getRequest({ url: APIConstant.SURVEY_MONTHLY_SUMMARY, extraParameters: { params } });

			const { result, error } = getValidDataFromResponse({ response, isRequired: false });

			if (!error && isValidArray(result)) {
				this.setMonthlySummaries(result);
			} else if (!error && result && !Array.isArray(result)) {
				this.setMonthlySummaries([result]);
			} else {
				this.setMonthlySummaries();
			}

			this.setIsLoadingMonthlySummaries();
			return this.monthlySummaries;
		} catch (error) {
			console.log('error :>> ', error);
			this.setMonthlySummaries();
			this.setIsLoadingMonthlySummaries();
			return [];
		}
	};

	/** Every individual submission behind one monthly aggregate row. */
	getSubmissionsForMonth = async (params = {}) => {
		try {
			this.setIsLoadingMonthSubmissions(true);
			this.setMonthSubmissions();

			const { response } = await getRequest({ url: APIConstant.SURVEY_SUBMISSION_HISTORY, extraParameters: { params } });

			const { result, error } = getValidDataFromResponse({ response, isRequired: false });

			const submissions = error ? [] : normalizeSubmissions(result);

			this.setMonthSubmissions(submissions);
			this.setIsLoadingMonthSubmissions();

			return submissions;
		} catch (error) {
			console.log('error :>> ', error);
			this.setMonthSubmissions();
			this.setIsLoadingMonthSubmissions();
			return [];
		}
	};

	setAssignedTemplates = (data = []) => {
		runInAction(() => {
			this.assignedTemplates = data;
		});
	};

	setIsLoadingAssignedTemplates = (value = false) => {
		runInAction(() => {
			this.isLoadingAssignedTemplates = value;
		});
	};

	setSubmissionTemplate = (data = null) => {
		runInAction(() => {
			this.submissionTemplate = data;
		});
	};

	setIsLoadingSubmissionTemplate = (value = false) => {
		runInAction(() => {
			this.isLoadingSubmissionTemplate = value;
		});
	};

	setCurrentMonthSummary = (data = null) => {
		runInAction(() => {
			this.currentMonthSummary = data;
		});
	};

	setIsLoadingCurrentMonthSummary = (value = false) => {
		runInAction(() => {
			this.isLoadingCurrentMonthSummary = value;
		});
	};

	setIsSubmittingSurvey = (value = false) => {
		runInAction(() => {
			this.isSubmittingSurvey = value;
		});
	};

	setMonthlySummaries = (data = []) => {
		runInAction(() => {
			this.monthlySummaries = data;
		});
	};

	setIsLoadingMonthlySummaries = (value = false) => {
		runInAction(() => {
			this.isLoadingMonthlySummaries = value;
		});
	};

	setMonthSubmissions = (data = []) => {
		runInAction(() => {
			this.monthSubmissions = data;
		});
	};

	setIsLoadingMonthSubmissions = (value = false) => {
		runInAction(() => {
			this.isLoadingMonthSubmissions = value;
		});
	};
}

export default SurveySubmissionStore;
